import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";
import { useCountdown } from "usehooks-ts";
import localforage from "localforage";
import { tokens } from "~/locales/tokens";
import { useUserPermissions } from "~/hooks/use-user-permissions";
import { useWindowFocus } from "~served/ui-comps/hooks/use-window-focus";
import { STORAGE_KEYS } from "~/libs/storage-keys";
import { useAuthCache } from "~/queries/use-auth-cache";
import { useGetCurrentVenue } from "~/queries/use-get-current-venue";
import { useLogout } from "~/store";
import { Dialog } from "~served/ui-comps/components/dialog";

export const LogoutModal = () => {
	const { reload, query, pathname } = useRouter();
	const { t } = useTranslation();

	const logout = useLogout();
	const { data: auth } = useAuthCache();
	const { data: venue } = useGetCurrentVenue();
	const permissions = useUserPermissions();
	const [isIdle, setIsIdle] = useState(false);
	// See: https://github.com/SupremeTechnopriest/react-idle-timer/pull/361
	// eslint-disable-next-line @typescript-eslint/unbound-method
	const { start: startIdleTimer, pause: pauseIdleTimer } = useIdleTimer({
		timeout: permissions.AUTO_LOGOUT_TIME,
		startManually: true,
		onIdle: () => setIsIdle(true),
		debounce: 1000,
	});
	const isWindowFocus = useWindowFocus();
	const [isLoggedOutInOtherTab, setIsLoggedOutInOtherTab] = useState(false);
	const [isLoggedInToDiffVenue, setIsLoggedInToDiffVenue] = useState(false);

	const verifyUserSession = async () => {
		if (!auth) return;

		const cachedToken = await localforage.getItem<string>(
			STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID,
		);
		if (!cachedToken) return setIsLoggedOutInOtherTab(true);

		const cachedVenueId = await localforage.getItem<string>(
			STORAGE_KEYS.VENUE_ID,
		);
		if (venue && cachedVenueId && cachedVenueId !== venue._id)
			return setIsLoggedInToDiffVenue(true);
	};

	useEffect(() => {
		void verifyUserSession();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isWindowFocus]);

	useEffect(() => {
		if (!auth) {
			pauseIdleTimer();
			return;
		}

		if (pathname === "/" && query.tab === "prep_stations") {
			pauseIdleTimer();
			return;
		}

		startIdleTimer();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth, query, pathname]);

	const [count, { start, stop, reset }] = useCountdown({
		interval: 1000,
		seconds: 10,
	});

	useEffect(() => {
		if (isIdle) start();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isIdle]);

	useEffect(() => {
		if (count === 0) {
			stop();
			reset();
			setIsIdle(false);
			void logout();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [count]);

	const handleClose = () => {
		stop();
		reset();
		setIsIdle(false);
		setIsLoggedOutInOtherTab(false);
		setIsLoggedInToDiffVenue(false);
	};

	if (isLoggedOutInOtherTab)
		return (
			<Dialog
				dialogProps={{ maxWidth: "xs" }}
				title="You were logged out"
				content="It appears you were logged out from somewhere else. If you have another tab open and unused for a while. That might be the cause."
				actionButtonProps={{
					children: "Log In Again",
					onClick: () => {
						setIsLoggedOutInOtherTab(false);
						void logout();
					},
				}}
			/>
		);

	if (isLoggedInToDiffVenue)
		return (
			<Dialog
				dialogProps={{ maxWidth: "xs" }}
				title="Signed in to another venue"
				content="It appears this device / browser is signed in to another venue. To make sure you're seeing the correct data. Reload the page."
				actionButtonProps={{
					children: "Reload",
					onClick: () => reload(),
				}}
			/>
		);

	if (isIdle)
		return (
			<Dialog
				dialogProps={{ maxWidth: "xs" }}
				title="You're about to be logged out"
				content={`You've been inactive for a while. To protect your account's security, we're logging you out in ${count} seconds.`}
				actionButtonProps={{
					children: t(tokens.global.cancel),
					onClick: handleClose,
				}}
			/>
		);

	return null;
};
